import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import {
  ImportFinding, ImportGroupOption, ImportLookup, ImportLookups, ImportPreview, ImportResult, ImportRunListItem,
  ImportSeriesPlan, ImportSeriesResult, ImportService, PlanAction, SeriesDecision
} from '@api/import.service';

/** The reviewer's choices for one meter, plus what the screen needs to edit them. */
interface MeterChoice extends SeriesDecision {
  seriesId: string;
  include: boolean;
  siteMode: 'existing' | 'new';
  siteFilter: string;
  /** For a new site: put it in an existing portal, or create a portal for it. */
  portalMode: 'existing' | 'new';
  groupMode: 'new' | 'existing';
  groups: ImportGroupOption[];
  groupsLoading: boolean;
}

/** One kind of data check, with every meter it flagged. */
interface CheckGroup {
  check: string;
  title: string;
  /** INFO is worth knowing but needs nothing doing, so it sorts last. */
  level: 'ERROR' | 'WARN' | 'INFO';
  findings: ImportFinding[];
}

/** One source store and its meters, total first and each sub-meter under its parent. */
interface StoreView {
  sourceSite: string;
  rows: { s: ImportSeriesPlan; depth: number; name: string }[];
  /** The first meter Carbon hasn't seen: its site choice is the store's, copied to the others. */
  lead?: ImportSeriesPlan;
  /** Set when every meter was matched before, so there is no site to choose. */
  matchedSite?: string;
}

/** What each check is called on screen, most worth acting on first. */
const CHECKS: { [check: string]: { title: string; info?: boolean } } = {
  balance: { title: 'Doesn\'t balance' },
  intervals: { title: 'Intervals out of line with the clock' },
  duplicates: { title: 'Intervals repeated' },
  negatives: { title: 'Negative values' },
  zero: { title: 'Reads zero for the whole period' },
  flatlines: { title: 'Stuck on one value for a day or more' },
  spikes: { title: 'Unusually high values' },
  gaps: { title: 'Missing readings' },
  estimated: { title: 'Estimated by the source', info: true },
};

/**
 * Import meter data exported from other platforms (FusionSolar, Enacto, standard CSV).
 * SuperAdmin only: the server refuses everyone else, and the menu item is hidden from them.
 *
 * Upload a file, see what the checks found and where each meter would go, choose a site for any
 * meter Carbon hasn't seen before, check the plan, apply. Every import can be undone from History.
 */
@Component({
  selector: 'app-modal-import',
  templateUrl: './modal-import.component.html',
  styleUrls: ['./modal-import.component.scss']
})
export class ModalImportComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  tab = 0;
  /** The files picked or dropped. Several are checked and imported together as one upload. */
  files: File[] = [];
  dragging = false;
  busy = '';
  /** The last thing the server refused or failed on, kept on screen until the next action. */
  error = '';

  lookups: ImportLookups = { sites: [], portals: [], categories: [] };
  preview: ImportPreview | null = null;
  choices: { [seriesId: string]: MeterChoice } = {};
  confirmNewSites = false;

  /** True when something changed since the last plan, so the plan on screen is out of date. */
  dirty = false;

  result: ImportResult | null = null;
  runs: ImportRunListItem[] = [];

  /** Built from the preview each time it changes. */
  checkGroups: CheckGroup[] = [];
  stores: StoreView[] = [];
  /** Meters whose group and category are open for editing. */
  editing = new Set<string>();

  constructor(public dialogRef: MatDialogRef<ModalImportComponent>, private api: ImportService) {
  }

  ngOnInit(): void {
    this.api.lookups$().subscribe(l => this.lookups = l, this.failed);
  }

  /** A refusal comes back as { message }, which is written for the reader, so show it as it is. */
  private failed = (e: any): void => {
    this.error = (e && e.error && e.error.message)
      || 'Something went wrong on the server. Try again, and if it keeps happening send the file to support.';
  }

  // ---- step 1: upload ------------------------------------------------------------------------

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    this.addFiles(event.dataTransfer ? event.dataTransfer.files : null);
  }

  /** Adds to what is already picked, so files can come from more than one folder. The same name and size is added once. */
  private addFiles(list: FileList | null): void {
    if (!list) return;
    for (const f of Array.from(list)) {
      if (!this.files.some(x => x.name === f.name && x.size === f.size)) this.files.push(f);
    }
    this.error = '';
  }

  removeFile(f: File): void {
    this.files = this.files.filter(x => x !== f);
  }

  upload(): void {
    if (!this.files.length) return;
    this.busy = this.files.length > 1 ? `Reading ${this.files.length} files` : 'Reading the file';
    this.error = '';
    this.result = null;
    this.api.preview$(this.files).pipe(finalize(() => this.busy = '')).subscribe(p => {
      this.preview = p;
      this.choices = {};
      this.confirmNewSites = false;
      for (const s of p.series) {
        this.choices[s.seriesId] = {
          seriesId: s.seriesId,
          include: s.state !== 'Skipped',
          siteMode: 'existing',
          siteFilter: '',
          portalMode: 'existing',
          groupMode: 'new',
          newLogicalGroupName: s.suggestedGroupName,
          categoryId: s.suggestedCategoryId,
          groups: [],
          groupsLoading: false,
        };
      }
      this.editing.clear();
      this.refreshViews();
      this.dirty = false;
    }, this.failed);
  }

  startAgain(): void {
    this.error = '';
    this.preview = null;
    this.result = null;
    this.files = [];
    this.choices = {};
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  // ---- step 2: choose where unmatched meters go ------------------------------------------------

  choice(s: ImportSeriesPlan): MeterChoice {
    return this.choices[s.seriesId];
  }

  needsChoices(s: ImportSeriesPlan): boolean {
    return !s.match && this.choice(s).include;
  }

  changed(): void {
    this.dirty = true;
  }

  filteredSites(s: ImportSeriesPlan): ImportLookup[] {
    const f = (this.choice(s).siteFilter || '').toLowerCase().trim();
    const suggested = new Set(s.siteSuggestions.map(x => x.siteId));
    const pool = this.lookups.sites.filter(x => !suggested.has(x.id));
    const hits = f.length < 2 ? [] : pool.filter(x => x.name.toLowerCase().includes(f) || String(x.id) === f);
    return hits.slice(0, 30);
  }

  siteName(id?: number): string {
    return this.lookups.sites.find(x => x.id === id)?.name ?? '';
  }

  /** The autocomplete's value is the site id; show its name. Typed text passes through. */
  siteNameFn = (v: number | string | null): string => typeof v === 'number' ? this.siteName(v) : (v ?? '');

  pickSite(s: ImportSeriesPlan, siteId: number): void {
    const c = this.choice(s);
    c.siteId = siteId;
    c.siteFilter = this.siteName(siteId);
    c.logicalGroupId = undefined;
    c.groupMode = 'new';
    this.loadGroups(s);
    this.syncStore(s);
    this.changed();
  }

  clearSite(s: ImportSeriesPlan): void {
    const c = this.choice(s);
    c.siteId = undefined;
    c.siteFilter = '';
    c.groups = [];
    c.logicalGroupId = undefined;
    this.syncStore(s);
    this.changed();
  }

  setSiteMode(s: ImportSeriesPlan, mode: 'existing' | 'new'): void {
    const c = this.choice(s);
    c.siteMode = mode;
    if (mode === 'new') {
      c.siteId = undefined;
      c.siteFilter = '';
      c.groups = [];
      c.logicalGroupId = undefined;
      c.groupMode = 'new';
      c.newSiteName = c.newSiteName || this.cleanSourceSite(s.sourceSite);
    } else {
      c.newSiteName = undefined;
      c.newSitePortalId = undefined;
      c.newPortalName = undefined;
      c.portalMode = 'existing';
    }
    this.syncStore(s);
    this.changed();
  }

  setPortalMode(s: ImportSeriesPlan, mode: 'existing' | 'new'): void {
    const c = this.choice(s);
    c.portalMode = mode;
    if (mode === 'new') c.newSitePortalId = undefined;
    else c.newPortalName = undefined;
    this.syncStore(s);
    this.changed();
  }

  /** "5790 MAYNOOTH NEW STORE (loc/5790)" reads better without the platform's own id. */
  cleanSourceSite(name: string): string {
    return name.replace(/\s*\(loc\/\d+\)\s*$/, '').trim().slice(0, 50);
  }

  emptyGroups(c: MeterChoice): ImportGroupOption[] {
    return c.groups.filter(g => g.mappedSeriesCount === 0);
  }

  /** Why "existing empty group" can't be picked, or empty when it can. */
  groupHint(c: MeterChoice): string {
    if (c.siteMode === 'new') return 'A new site has no groups yet.';
    if (c.groupsLoading) return 'Loading the site\'s groups...';
    if (!c.groups.length) return 'This site has no logical groups yet.';
    if (!this.emptyGroups(c).length) {
      return `None of this site's ${c.groups.length} group(s) is empty. A group that already counts a meter can't take another, because they would be added together.`;
    }
    return '';
  }

  private loadGroups(s: ImportSeriesPlan): void {
    const c = this.choice(s);
    if (!c.siteId) return;
    c.groupsLoading = true;
    this.api.groups$(c.siteId).pipe(finalize(() => c.groupsLoading = false)).subscribe(g => {
      c.groups = g;
      this.syncStore(s);
    });
  }

  /** The site is chosen once per store: copy it to every other meter from the same source site that Carbon hasn't seen. */
  syncStore(s: ImportSeriesPlan): void {
    const from = this.choice(s);
    for (const other of this.preview?.series ?? []) {
      if (other.seriesId === s.seriesId || other.match || other.sourceSite !== s.sourceSite) continue;
      const c = this.choice(other);
      if (c.siteId !== from.siteId || from.siteMode === 'new') {
        c.logicalGroupId = undefined;
        c.groupMode = 'new';
      }
      c.siteMode = from.siteMode;
      c.siteId = from.siteId;
      c.siteFilter = from.siteFilter;
      c.newSiteName = from.newSiteName;
      c.newSitePortalId = from.newSitePortalId;
      c.newPortalName = from.newPortalName;
      c.portalMode = from.portalMode;
      c.groups = from.groups;
    }
  }

  /** A new site name typed for the store. */
  newSiteNameChanged(s: ImportSeriesPlan): void {
    this.syncStore(s);
    this.changed();
  }

  toggleEdit(s: ImportSeriesPlan): void {
    if (this.editing.has(s.seriesId)) this.editing.delete(s.seriesId);
    else this.editing.add(s.seriesId);
  }

  // ---- the review's layout -------------------------------------------------------------------

  private refreshViews(): void {
    const p = this.preview;
    if (!p) {
      this.checkGroups = [];
      this.stores = [];
      return;
    }

    const groups: { [check: string]: CheckGroup } = {};
    for (const f of p.findings) {
      const known = CHECKS[f.check];
      const level = f.level === 'ERROR' ? 'ERROR' : known && known.info ? 'INFO' : 'WARN';
      const g = groups[f.check] = groups[f.check] || { check: f.check, title: known ? known.title : f.check, level, findings: [] };
      if (level === 'ERROR') g.level = 'ERROR';
      g.findings.push(f);
    }
    const order = Object.keys(CHECKS);
    const rank = (g: CheckGroup) => (g.level === 'ERROR' ? 0 : g.level === 'WARN' ? 100 : 200) + (order.indexOf(g.check) < 0 ? 99 : order.indexOf(g.check));
    this.checkGroups = Object.values(groups).sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));

    const bySite = new Map<string, ImportSeriesPlan[]>();
    for (const s of p.series) bySite.set(s.sourceSite, [...(bySite.get(s.sourceSite) || []), s]);
    this.stores = Array.from(bySite.entries()).map(([sourceSite, series]) => {
      // Parent before its children, whatever the names: the separators sort below any letter, the measure's below the path's.
      const sortKey = (s: ImportSeriesPlan) =>
        (s.sourceMeter === 'site total' ? '0' : '1' + s.sourceMeter.toLowerCase().split(' / ').join('\u0002')) + '\u0001' + s.measure;
      const rows = [...series].sort((a, b) => sortKey(a) < sortKey(b) ? -1 : sortKey(a) > sortKey(b) ? 1 : 0).map(s => ({
        s,
        depth: s.sourceMeter === 'site total' ? 0 : s.sourceMeter.split(' / ').length,
        name: (s.sourceMeter === 'site total' ? 'Store total' : s.sourceMeter.split(' / ').pop() as string)
          + (s.measure === 'consumption' ? '' : ` (${this.measureLabel(s)})`),
      }));
      const lead = rows.map(r => r.s).find(s => !s.match);
      const matchedSites = Array.from(new Set(series.filter(s => s.match).map(s => s.match!.siteName)));
      return { sourceSite, rows, lead, matchedSite: !lead && matchedSites.length === 1 ? matchedSites[0] : undefined };
    });
  }

  /** The meter a finding is about, in the screen's words. */
  findingMeter(f: ImportFinding): string {
    const s = (this.preview?.series ?? []).find(x => f.series === `${x.sourceSite} / ${x.sourceMeter} / ${x.utility} ${x.measure} (${x.unit})`);
    if (!s) return f.series;
    const meter = s.sourceMeter === 'site total' ? 'Store total' : s.sourceMeter;
    return this.stores.length > 1 ? `${this.cleanSourceSite(s.sourceSite)}: ${meter}` : meter;
  }

  get actionableChecks(): number {
    return this.checkGroups.filter(g => g.level !== 'INFO').length;
  }

  get actionableTitles(): string {
    return this.checkGroups.filter(g => g.level !== 'INFO').map(g => g.title.toLowerCase()).join(', ');
  }

  /** Where the store's meters are going, for the header: the matched site, the picked site or the new one. */
  storeTarget(st: StoreView): string {
    if (st.matchedSite) return st.matchedSite;
    if (!st.lead) return '';
    const c = this.choice(st.lead);
    if (c.siteMode === 'new') return c.newSiteName ? `${c.newSiteName} (new)` : '';
    return c.siteId ? `${this.siteName(c.siteId)} (${c.siteId})` : '';
  }

  /** The group a meter goes to, as it stands now. */
  groupLabel(s: ImportSeriesPlan): string {
    if (s.match) return s.match.logicalGroupName;
    const c = this.choice(s);
    if (c.groupMode === 'existing') return c.groups.find(g => g.logicalGroupId === c.logicalGroupId)?.name ?? 'Pick a group';
    return c.newLogicalGroupName || s.suggestedGroupName;
  }

  categoryName(s: ImportSeriesPlan): string {
    if (s.match) return '';
    const id = this.choice(s).categoryId;
    return id ? this.lookups.categories.find(c => c.id === id)?.name ?? `#${id}` : '';
  }

  stackLabel(s: ImportSeriesPlan): string {
    switch (s.stack) {
      case 'total': return 'Combined total';
      case 'stacked': return 'Stacked';
      case 'in-parent': return 'In parent';
      default: return '';
    }
  }

  stackHelp(s: ImportSeriesPlan): string {
    switch (s.stack) {
      case 'total': return 'The Combined chart hangs off this total. Its Remainder is this total minus the stacked assets: what no sub-meter covers.';
      case 'stacked': return 'Stacked on the Combined chart with the store\'s other assets.';
      case 'in-parent': return `Left out of the Combined stack because ${s.stackParent} already counts it, so nothing is counted twice. It still has its own chart.`;
      default: return '';
    }
  }

  warnings(s: ImportSeriesPlan): PlanAction[] {
    return s.actions.filter(a => a.kind === 'warn');
  }

  warningText(s: ImportSeriesPlan): string {
    return this.warnings(s).map(w => w.text).join(' ');
  }

  canEdit(s: ImportSeriesPlan): boolean {
    return !s.match && this.choice(s).include;
  }

  // ---- the result ----------------------------------------------------------------------------

  /** Counts of meters by result status, in a fixed order. */
  get resultCounts(): { label: string; count: number; bad?: boolean }[] {
    const r = this.result;
    if (!r) return [];
    const by = (st: string) => r.series.filter(x => x.status === st).length;
    return [
      { label: 'imported', count: by('Written') },
      { label: 'set up, no readings', count: by('SetUp') },
      { label: 'undone', count: by('Undone') },
      { label: 'left out', count: by('Skipped') },
      { label: 'failed', count: by('Failed') + by('UndoFailed'), bad: true },
    ].filter(x => x.count > 0);
  }

  get resultSites(): string[] {
    return this.result ? Array.from(new Set(this.result.series.map(x => x.siteName).filter((n): n is string => !!n))) : [];
  }

  get resultCreated(): string[] {
    const r = this.result;
    if (!r) return [];
    const out = r.messages.filter(m => m.startsWith('Created '));
    const groups = r.series.filter(x => x.steps.some(st => st.startsWith('Created logical group'))).length;
    if (groups) out.push(`Created ${groups} logical group${groups === 1 ? '' : 's'}.`);
    return out;
  }

  get resultReadings(): { stored: number; replaced: number; hours: number } {
    const s = this.result ? this.result.series : [];
    return {
      stored: s.reduce((n, x) => n + x.rawRowsInserted + x.rawRowsReplaced, 0),
      replaced: s.reduce((n, x) => n + x.rawRowsReplaced, 0),
      hours: s.reduce((n, x) => n + (x.hoursWritten || 0), 0),
    };
  }

  /** Things a person should look at: failures, set-up-only meters, and messages that aren't just "created". */
  get resultAttention(): string[] {
    const r = this.result;
    if (!r) return [];
    const out: string[] = [];
    for (const x of r.series.filter(y => y.status === 'Failed' || y.status === 'UndoFailed')) out.push(`${this.resultMeter(x)} failed: ${x.detail || 'no detail'}`);
    for (const m of r.messages.filter(y => /fail|stopped/i.test(y))) out.push(m);
    const setUp = r.series.filter(x => x.status === 'SetUp');
    if (setUp.length) out.push(`${setUp.map(x => this.resultMeter(x)).join(', ')}: set up with no readings. Import their meter files to fill them.`);
    return out;
  }

  get resultNotes(): string[] {
    return this.result ? this.result.messages.filter(m => !m.startsWith('Created ') && !/fail|stopped/i.test(m)) : [];
  }

  /** The meter a result line is about, named as on the review screen. */
  resultMeter(x: ImportSeriesResult): string {
    const s = (this.preview?.series ?? []).find(p => p.seriesId === x.seriesId);
    if (!s) return this.plain(x.label);
    const meter = s.sourceMeter === 'site total' ? 'Store total' : s.sourceMeter;
    return (this.stores.length > 1 ? `${this.cleanSourceSite(s.sourceSite)}: ` : '') + meter + (s.measure === 'consumption' ? '' : ` (${this.measureLabel(s)})`);
  }

  // ---- portal categories ---------------------------------------------------------------------

  /** Categories that suit an imported meter (Consumption report). */
  get recommendedCategories(): ImportLookup[] {
    return this.lookups.categories.filter(c => c.recommended);
  }

  /** Everything else, each with the report type that makes it different. */
  get otherCategories(): ImportLookup[] {
    return this.lookups.categories.filter(c => !c.recommended);
  }

  categoryLabel(c: ImportLookup, withReport: boolean): string {
    const twin = this.lookups.categories.filter(x => x.recommended === c.recommended && x.name === c.name).length > 1;
    let label = c.name;
    if (withReport && c.detail) label += ` [${c.detail}]`;
    if (twin) label += ` (#${c.id})`;
    return label;
  }

  // ---- the plan --------------------------------------------------------------------------------

  private decisions() {
    // A meter matched before has no choices on screen, and sending the defaults behind them (the
    // suggested group name) would read as a request to move it, which the server refuses.
    const matched = new Set((this.preview ? this.preview.series : []).filter(s => s.state === 'Matched').map(s => s.seriesId));
    return {
      confirmNewSites: this.confirmNewSites,
      series: Object.values(this.choices).map(c => matched.has(c.seriesId) ? { seriesId: c.seriesId, include: c.include } as SeriesDecision : ({
        seriesId: c.seriesId,
        include: c.include,
        siteId: c.siteMode === 'existing' ? c.siteId : undefined,
        newSiteName: c.siteMode === 'new' ? (c.newSiteName || '').trim() : undefined,
        newSitePortalId: c.siteMode === 'new' && c.portalMode === 'existing' ? c.newSitePortalId : undefined,
        newPortalName: c.siteMode === 'new' && c.portalMode === 'new' ? (c.newPortalName || '').trim() : undefined,
        logicalGroupId: c.groupMode === 'existing' ? c.logicalGroupId : undefined,
        newLogicalGroupName: c.groupMode === 'new' ? (c.newLogicalGroupName || '').trim() : undefined,
        categoryId: c.categoryId || undefined,
      } as SeriesDecision)),
    };
  }

  checkPlan(): void {
    if (!this.preview) return;
    this.busy = 'Checking the plan';
    this.error = '';
    this.api.plan$(this.preview.runId, this.decisions()).pipe(finalize(() => this.busy = '')).subscribe(p => {
      this.preview = p;
      this.refreshViews();
      this.dirty = false;
    }, this.failed);
  }

  get newSiteNames(): string[] {
    return Array.from(new Set(Object.values(this.choices)
      .filter(c => c.include && c.siteMode === 'new' && c.newSiteName)
      .map(c => (c.newSiteName || '').trim())));
  }

  get newPortalNames(): string[] {
    return Array.from(new Set(Object.values(this.choices)
      .filter(c => c.include && c.siteMode === 'new' && c.portalMode === 'new' && c.newPortalName)
      .map(c => (c.newPortalName || '').trim())));
  }

  get includedCount(): number {
    return Object.values(this.choices).filter(c => c.include).length;
  }

  get canApply(): boolean {
    return !!this.preview && this.preview.canApply && !this.dirty && !this.busy
      && (this.newSiteNames.length === 0 || this.confirmNewSites);
  }

  apply(): void {
    if (!this.preview || !this.canApply) return;
    this.busy = 'Importing. Large files can take a minute or two';
    this.error = '';
    this.api.apply$(this.preview.runId, this.decisions()).pipe(finalize(() => this.busy = '')).subscribe(r => {
      this.result = r;
      this.tab = 0;
      this.loadRuns();
    }, this.failed);
  }

  // ---- history and undo ----------------------------------------------------------------------

  onTabChange(index: number): void {
    this.tab = index;
    if (index === 1) this.loadRuns();
  }

  loadRuns(): void {
    this.api.runs$(50).subscribe(r => this.runs = r);
  }

  undo(runId: number): void {
    if (!confirm(`Undo upload ${runId}? Its readings are removed and the hourly figures rebuilt. The meters' set-up is kept.`)) return;
    this.busy = `Undoing upload ${runId}`;
    this.error = '';
    this.api.undo$(runId).pipe(finalize(() => this.busy = '')).subscribe(r => {
      this.result = r;
      this.tab = 0;
      this.loadRuns();
    }, this.failed);
  }

  // ---- display helpers -----------------------------------------------------------------------

  meterLabel(s: ImportSeriesPlan): string {
    const meter = s.sourceMeter === 'site total' ? '' : ` / ${s.sourceMeter}`;
    return `${s.sourceSite}${meter}`;
  }

  /** Measures are stored as words joined by underscores (generation_pv); show them as words. */
  plain(label: string): string {
    return label.replace('electricity net_import', 'grid, import minus export').replace(/_/g, ' ');
  }

  measureLabel(s: ImportSeriesPlan): string {
    if (s.measure === 'net_import') return 'grid, import minus export';
    return `${s.utility} ${s.measure.replace(/_/g, ' ')}`;
  }

  stateLabel(s: ImportSeriesPlan): string {
    switch (s.state) {
      case 'Matched': return 'Matched before';
      case 'NeedsMatch': return 'Choose a site';
      case 'Ready': return 'Ready';
      case 'Refused': return 'Can\'t import';
      default: return 'Left out';
    }
  }

  /** What a file's check result means, for the tooltip. */
  checkHelp(status: string): string {
    switch (status) {
      case 'PASS': return 'PASS: the checks found nothing to flag. The file can be imported.';
      case 'WARN': return 'WARN: the file can be imported, but the checks found something worth a look, such as gaps, long flat runs or estimated readings. They are listed under Data checks.';
      case 'FAIL': return 'FAIL: the checks found a problem that would make the figures wrong, such as duplicate or misaligned intervals. The file can\'t be imported until it is fixed.';
      default: return '';
    }
  }

  /** What an upload's status means, for the History tab. */
  statusHelp(status: string): string {
    switch (status) {
      case 'Previewed': return 'Checked only. The file was uploaded and checked, but nothing was imported into Carbon.';
      case 'Applying': return 'An import is running now.';
      case 'Applied': return 'Imported. The readings are in Carbon. Undo removes them again.';
      case 'AppliedWithErrors': return 'Some meters were imported and some failed. The failed ones show their reason in the result.';
      case 'Failed': return 'Nothing was imported.';
      case 'Undone': return 'Imported and then undone. The readings were removed; the set-up and saved matches are kept.';
      case 'UndoPartial': return 'The undo only partly worked. Undo it again.';
      default: return '';
    }
  }

  actionLabel(a: PlanAction): string {
    switch (a.kind) {
      case 'create': return 'Create';
      case 'use': return 'Existing';
      case 'overwrite': return 'Overwrite';
      case 'warn': return 'Check';
      default: return 'Note';
    }
  }
}
